import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A refused form says so at the button - WAIT-PL ruling 1 (Craig's finding F-1).
 *
 * On a long form the top-of-page message and the field errors sit above the
 * fold. Pressing the button made it flicker and nothing visible changed, which
 * reads as a broken button. So a refused submit now does two things:
 *
 *   1. **moves to the first problem**: scrolls the first field with an error
 *      into view, in page order, and puts the cursor in it. Smoothly, unless the
 *      browser asks for reduced motion;
 *   2. **says so at the button**, in one red line. For a refusal that is about
 *      a field, the line below. For one that is not - the server's own
 *      sentence, "We could not place your order." - that sentence, because
 *      otherwise the same thing happens with no field to jump to.
 *
 * The top-of-page message and the per-field messages are left exactly as they
 * were. **One helper for the four forms that say "Please check the highlighted
 * fields."**: the order form, sign-up, Settings and the project form.
 *
 * "A field with an error" is anything in the form marked `aria-invalid`, which
 * every field control in the portal already sets when it shows an error. It is
 * read off the rendered page rather than from each form's error object, so
 * "first" is what the person sees first, whatever order the object is in.
 */

export const FIELDS_REFUSED =
  "Some details need fixing. We've taken you to the first one.";

/** Said instead when no field could be found to go to. */
export const FIELDS_REFUSED_NOWHERE = 'Some details need fixing. The messages above say what.';

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** Scrolls to and focuses the first field marked invalid. False when there is none. */
export function focusFirstInvalid(form: HTMLElement | null): boolean {
  const target = form?.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (!target) return false;

  // Guarded calls: jsdom and older browsers lack one or both.
  target.scrollIntoView?.({
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    block: 'center',
  });
  target.focus({ preventScroll: true });
  return true;
}

/**
 * C-120: the message a form shows under each field, keyed by field. A field
 * with nothing to say is absent or `undefined`.
 */
export type FieldErrors<K extends string> = Partial<Record<K, string | undefined>>;

/** True when any field has a message, so the press is refused. */
export function anyFieldError<K extends string>(errors: FieldErrors<K>): boolean {
  return Object.values<string | undefined>(errors).some((message) => message !== undefined);
}

export interface SubmitRefusal<T extends HTMLElement = HTMLFormElement> {
  /**
   * Put on the `<form>`. C-120: or on the `<div>` around a panel's fields,
   * where the panel is not a form on purpose. A reason box inside a form
   * submits on Enter, and on a money button that would skip the press.
   */
  formRef: React.RefObject<T | null>;
  /** What the line by the button says, if anything. */
  message: string | null;
  /** Call at the start of every submit: the line clears on the next one. */
  clear: () => void;
  /** Call in the same update as the field errors are set. */
  refuseFields: () => void;
  /** Call with a refusal that is not about any one field. */
  refuse: (sentence: string) => void;
}

export function useSubmitRefusal<
  T extends HTMLElement = HTMLFormElement,
>(): SubmitRefusal<T> {
  const formRef = useRef<T | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // Bumped on every field refusal, so two refusals in a row both move the page.
  const [fieldRefusals, setFieldRefusals] = useState(0);

  /*
   * After the render that shows the field errors, not before: until then there
   * is nothing marked invalid to move to.
   */
  useEffect(() => {
    if (fieldRefusals === 0) return;
    if (!focusFirstInvalid(formRef.current)) setMessage(FIELDS_REFUSED_NOWHERE);
  }, [fieldRefusals]);

  const clear = useCallback(() => {
    setMessage(null);
  }, []);

  const refuseFields = useCallback(() => {
    setMessage(FIELDS_REFUSED);
    setFieldRefusals((count) => count + 1);
  }, []);

  const refuse = useCallback((sentence: string) => {
    setMessage(sentence);
  }, []);

  return { formRef, message, clear, refuseFields, refuse };
}

/** The one red line, placed directly above the submit button. */
export function SubmitRefusalLine({ message }: { message: string | null }): React.JSX.Element | null {
  if (!message) return null;
  return (
    <p aria-live="assertive" className="text-sm font-medium text-danger">
      {message}
    </p>
  );
}
